/**
 * VAL-FILE-SPLIT-1 — split out of ValuationProfileSelect.tsx under
 * docs/FILE_SIZE_POLICY.md, needed to hit the page's <=300-line override.
 * The Engagement card's full form body — methodology-basis display, company
 * / period / valuation-date, engagement particulars, and the SSVS No. 1
 * deliverable choice. Submit handler and state stay on the page.
 */
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ValuationProfile, ValueKind } from '@/types/valuation'
import { STANDARDS_OF_VALUE } from './_IntakeParticulars'
import { basisLabel } from './_shared'
import { multiple } from '@/utils/format'

export function CreateEngagementForm({
  selectedProfile,
  onChooseProfile,
  companyName, setCompanyName,
  periodLabel, setPeriodLabel,
  effectiveDate, setEffectiveDate,
  purpose, setPurpose,
  intendedUsers, setIntendedUsers,
  standardOfValue, setStandardOfValue,
  premiseOfValue, setPremiseOfValue,
  subjectInterest, setSubjectInterest,
  valueKind, setValueKind,
  canConclude,
  isOwner = false,
}: {
  selectedProfile: ValuationProfile | null
  onChooseProfile: () => void
  companyName: string
  setCompanyName: (v: string) => void
  periodLabel: string
  setPeriodLabel: (v: string) => void
  effectiveDate: string
  setEffectiveDate: (v: string) => void
  purpose: string
  setPurpose: (v: string) => void
  intendedUsers: string
  setIntendedUsers: (v: string) => void
  standardOfValue: string
  setStandardOfValue: (v: string) => void
  premiseOfValue: string
  setPremiseOfValue: (v: string) => void
  subjectInterest: string
  setSubjectInterest: (v: string) => void
  valueKind: ValueKind
  setValueKind: (v: ValueKind) => void
  canConclude: boolean
  /** JOIN-MDEMO 3d: R3 plain words for the seller only (OWNER_APPROVALS.log 2026-10-26 05:36). */
  isOwner?: boolean
}) {
  return (
    <>
      <div className="rounded-lg border border-pal-line p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-pal-ink-muted">
              {isOwner ? 'Industry' : 'Methodology basis'}
            </p>
            {selectedProfile ? (
              <>
                {/* JOIN-MDEMO 3b: the methodology version is never shown; it stays stored (OWNER_APPROVALS.log 2026-10-25 20:19-20:22). */}
                <p className="mt-1 font-medium text-pal-ink">{selectedProfile.display_name}</p>
                <p className="mt-1 text-xs text-pal-ink-muted">
                  Premise: {selectedProfile.premise_label ?? selectedProfile.premise_default}
                </p>
                <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-pal-ink-soft">
                  {selectedProfile.multiples.map(m => (
                    <div key={m.key} className="flex gap-1">
                      <dt>
                        {m.label ?? m.key}{m.is_tiered ? ` (tiered by ${basisLabel(String(m.tier_basis))})` : ''}:
                      </dt>
                      <dd>{multiple(m.envelope_low)} – {multiple(m.envelope_high)}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : (
              <p className="mt-1 text-sm text-pal-warning-text">{isOwner ? 'No industry chosen yet.' : 'No industry profile chosen yet.'}</p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onChooseProfile}>
            {selectedProfile ? 'Change' : isOwner ? 'Choose industry' : 'Choose profile'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="subject_company_name">Subject company *</Label>
          <Input
            id="subject_company_name"
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            placeholder="Acme Plumbing LLC"
            required
          />
        </div>
        <div>
          <Label htmlFor="period_label">Period</Label>
          <Input
            id="period_label"
            value={periodLabel}
            onChange={e => setPeriodLabel(e.target.value)}
            placeholder="TTM ended 31 Dec"
          />
        </div>
        <div>
          <Label htmlFor="effective_date">Valuation date (effective date)</Label>
          <Input
            id="effective_date"
            type="date"
            value={effectiveDate}
            onChange={e => setEffectiveDate(e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="purpose">Purpose of the engagement *</Label>
          <Input
            id="purpose"
            value={purpose}
            onChange={e => setPurpose(e.target.value)}
            placeholder="Gift tax reporting"
            required
          />
        </div>
        <div>
          <Label htmlFor="intended_users">Intended users *</Label>
          <Input
            id="intended_users"
            value={intendedUsers}
            onChange={e => setIntendedUsers(e.target.value)}
            placeholder="The client and its tax counsel"
            required
          />
        </div>
        <div>
          <Label htmlFor="standard_of_value">Standard of value *</Label>
          <select
            id="standard_of_value"
            className="h-9 w-full rounded-md border border-pal-line-strong bg-pal-white px-2 text-sm"
            value={standardOfValue}
            onChange={e => setStandardOfValue(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {STANDARDS_OF_VALUE.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="premise_of_value">Premise of value *</Label>
          <Input
            id="premise_of_value"
            value={premiseOfValue}
            onChange={e => setPremiseOfValue(e.target.value)}
            placeholder="Going concern"
            required
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="subject_interest">Subject interest *</Label>
          <Input
            id="subject_interest"
            value={subjectInterest}
            onChange={e => setSubjectInterest(e.target.value)}
            placeholder="100% of the member interests of Acme Plumbing LLC"
            required
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-pal-ink-body">{isOwner ? 'What you will receive' : 'Deliverable (SSVS No. 1)'}</legend>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-pal-line p-3 hover:bg-pal-surface-subtle">
          <input
            type="radio"
            name="value_kind"
            className="mt-1"
            checked={valueKind === 'calculated'}
            onChange={() => setValueKind('calculated')}
          />
          <span className="text-sm">
            <span className="font-medium text-pal-ink">Calculation engagement</span>
            <span className="block text-xs text-pal-ink-muted">
              Produces a <em>calculated value</em>. Explicitly not a conclusion of value.
            </span>
          </span>
        </label>
        <label
          className={`flex items-start gap-3 rounded-lg border border-pal-line p-3
            ${canConclude ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60'}`}
        >
          <input
            type="radio"
            name="value_kind"
            className="mt-1"
            disabled={!canConclude}
            checked={valueKind === 'concluded'}
            onChange={() => setValueKind('concluded')}
          />
          <span className="text-sm">
            <span className="font-medium text-pal-ink">Valuation engagement</span>
            <span className="block text-xs text-pal-ink-muted">
              Produces a <em>conclusion of value</em>. Credentialed Valuation Signer only, and fixed at
              creation — there is no path that promotes a calculation into a conclusion later.
            </span>
            {!canConclude && (
              <span className="mt-1 block text-xs text-pal-warning-text">
                Your role may run calculation engagements only.
              </span>
            )}
          </span>
        </label>
      </fieldset>
    </>
  )
}
